if (!customElements.get('cbm-public-schedule')) {
  customElements.define('cbm-public-schedule', class CbmPublicSchedule extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cbmInitialized === 'true') return;
      this.dataset.cbmInitialized = 'true';

      this.timezone = 'America/Los_Angeles';
      this.scheduleUrl = this.dataset.scheduleUrl || '';
      this.packageUrl = this.dataset.packageUrl || '/pages/buy-courses?state=nevada&product_type=packages';
      this.catalogUrl = this.dataset.catalogUrl || '/pages/buy-courses';
      this.contactUrl = this.dataset.contactUrl || '/pages/contact';
      this.labels = this.parseLabels();
      this.productMap = this.readProductMap();
      this.events = { all: [], ce: [], free: [] };
      this.months = { all: '', ce: '', free: '' };
      this.activeGroup = this.initialGroup();

      this.tabs = Array.from(this.querySelectorAll('[data-cbm-schedule-tab]'));
      this.todayButton = this.querySelector('[data-cbm-schedule-today]');
      this.prevButton = this.querySelector('[data-cbm-schedule-prev]');
      this.nextButton = this.querySelector('[data-cbm-schedule-next]');
      this.monthLabel = this.querySelector('[data-cbm-schedule-month]');
      this.status = this.querySelector('[data-cbm-schedule-status]');
      this.calendar = this.querySelector('[data-cbm-schedule-calendar]');
      this.weekdays = this.querySelector('[data-cbm-schedule-weekdays]');
      this.grid = this.querySelector('[data-cbm-schedule-grid]');
      this.agenda = this.querySelector('[data-cbm-schedule-agenda]');
      this.dialog = this.querySelector('[data-cbm-schedule-dialog]');
      this.dialogClose = this.querySelector('[data-cbm-schedule-close]');

      this.bind();
      this.renderWeekdays();
      this.load();
    }

    parseLabels() {
      const node = this.querySelector('[data-cbm-schedule-labels]');
      try {
        return JSON.parse(node?.textContent || '{}');
      } catch (_) {
        return {};
      }
    }

    readProductMap() {
      const map = {};
      this.querySelectorAll('[data-cbm-schedule-product]').forEach((node) => {
        const code = node.dataset.courseCode || '';
        if (!code || map[code]) return;
        map[code] = {
          url: node.dataset.productUrl || '',
          title: node.dataset.productTitle || '',
          available: node.dataset.productAvailable !== 'false',
        };
      });
      return map;
    }

    initialGroup() {
      const params = new URLSearchParams(window.location.search);
      const view = params.get('schedule');
      if (view === 'free' || window.location.hash === '#free') return 'free';
      if (view === 'ce') return 'ce';
      return 'all';
    }

    bind() {
      this.tabs.forEach((button) => button.addEventListener('click', () => {
        const requestedGroup = button.dataset.cbmScheduleTab;
        this.activeGroup = requestedGroup === 'ce' || requestedGroup === 'free' ? requestedGroup : 'all';
        this.ensureMonth(this.activeGroup);
        const url = new URL(window.location.href);
        url.searchParams.delete('view');
        if (this.activeGroup === 'all') url.searchParams.delete('schedule');
        else url.searchParams.set('schedule', this.activeGroup);
        url.hash = '';
        window.history.replaceState({}, '', url);
        this.render();
      }));

      this.todayButton?.addEventListener('click', () => {
        this.months[this.activeGroup] = this.currentPacificMonth();
        this.render();
      });

      this.prevButton?.addEventListener('click', () => this.shiftMonth(-1));
      this.nextButton?.addEventListener('click', () => this.shiftMonth(1));
      this.dialogClose?.addEventListener('click', () => this.dialog?.close());
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

    }

    async load() {
      this.setStatus(this.labels.loading || 'Loading schedule...');
      if (!this.scheduleUrl) return this.fail();

      try {
        const response = await fetch(this.scheduleUrl, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error('schedule request failed');
        const data = await response.json();
        this.timezone = data?.timezone || this.timezone;
        this.events.ce = this.paidEvents(data);
        this.events.free = this.freeEvents(data);
        this.events.all = this.upcoming([...this.events.ce, ...this.events.free]);
        this.ensureMonth('all');
        this.ensureMonth('ce');
        this.ensureMonth('free');
        this.calendar?.setAttribute('aria-busy', 'false');
        this.render();
      } catch (error) {
        console.error('CE public schedule error:', error);
        this.fail();
      }
    }

    fail() {
      this.calendar?.setAttribute('aria-busy', 'false');
      this.setStatus(this.labels.load_error || 'Schedule temporarily unavailable.');
      if (this.grid) this.grid.innerHTML = '';
      if (this.agenda) this.agenda.innerHTML = '<div class="cbm-public-schedule__agenda-empty">' + this.escape(this.labels.load_error || 'Schedule temporarily unavailable.') + '</div>';
    }

    paidEvents(data) {
      const designations = {
        'CE.7027000-RE': 'Agency',
        'CE.7028000-RE': 'Contracts 1',
        'CE.7029000-RE': 'Contracts 2',
        'CE.7037000-RE': 'Ethics',
        'CE.7085000-RE': 'Risk Reduction',
        'CE.7386000-RE': 'Law & Legislation',
      };
      const events = [];
      Object.entries(data?.courses || {}).forEach(([code, course]) => {
        (course?.events || []).forEach((event) => {
          events.push({
            ...event,
            group: 'ce',
            course_code: code,
            title: course?.title || this.productMap[code]?.title || code,
            designation: designations[code] || 'Live CE',
            ce_hours: 3,
            delivery: 'Zoom Live',
            product: this.productMap[code] || null,
          });
        });
      });
      return this.upcoming(events);
    }

    freeEvents(data) {
      return this.upcoming((data?.public_free_events || []).map((event) => ({
        ...event,
        group: 'free',
        delivery: event.delivery || 'In-Person',
      })));
    }

    upcoming(events) {
      const now = Date.now();
      return events
        .filter((event) => {
          const start = new Date(event?.start || '').getTime();
          return Number.isFinite(start) && start > now;
        })
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    }

    currentPacificMonth() {
      const parts = new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        timeZone: this.timezone,
      }).formatToParts(new Date());
      const get = (type) => parts.find((part) => part.type === type)?.value || '';
      return get('year') + '-' + get('month');
    }

    currentPacificDateKey() {
      const parts = new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: this.timezone,
      }).formatToParts(new Date());
      const get = (type) => parts.find((part) => part.type === type)?.value || '';
      return [get('year'), get('month'), get('day')].join('-');
    }

    eventDateKey(event) {
      const value = String(event?.start || '');
      const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
      return match ? match[1] : '';
    }

    ensureMonth(group) {
      if (this.months[group]) return;
      const current = this.currentPacificMonth();
      const hasCurrent = this.events[group].some((event) => this.eventDateKey(event).slice(0, 7) === current);
      this.months[group] = hasCurrent
        ? current
        : (this.eventDateKey(this.events[group][0]).slice(0, 7) || current);
    }

    shiftMonth(delta) {
      this.ensureMonth(this.activeGroup);
      const [year, month] = this.months[this.activeGroup].split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1 + delta, 1));
      this.months[this.activeGroup] = date.getUTCFullYear() + '-' + String(date.getUTCMonth() + 1).padStart(2, '0');
      this.render();
    }

    renderWeekdays() {
      if (!this.weekdays) return;
      const base = new Date(Date.UTC(2026, 0, 4));
      this.weekdays.innerHTML = Array.from({ length: 7 }, (_, index) => {
        const date = new Date(base.getTime() + index * 86400000);
        const label = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(date);
        return '<span class="cbm-public-schedule__weekday">' + this.escape(label) + '</span>';
      }).join('');
    }

    render() {
      this.ensureMonth(this.activeGroup);
      this.tabs.forEach((button) => {
        const active = button.dataset.cbmScheduleTab === this.activeGroup;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      const monthKey = this.months[this.activeGroup];
      const [year, month] = monthKey.split('-').map(Number);
      if (this.monthLabel) {
        this.monthLabel.textContent = new Intl.DateTimeFormat('en-US', {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(new Date(Date.UTC(year, month - 1, 1)));
      }

      this.renderGrid(year, month);
      this.renderAgenda(monthKey);
      const monthEvents = this.events[this.activeGroup].filter((event) => this.eventDateKey(event).slice(0, 7) === monthKey);
      this.setStatus(String(monthEvents.length) + ' ' + (this.labels.events_count || 'upcoming classes'));
    }

    renderGrid(year, month) {
      if (!this.grid) return;
      const first = new Date(Date.UTC(year, month - 1, 1));
      const firstWeekday = first.getUTCDay();
      const start = new Date(Date.UTC(year, month - 1, 1 - firstWeekday));
      const currentMonth = String(month).padStart(2, '0');
      const todayKey = this.currentPacificDateKey();

      this.grid.innerHTML = Array.from({ length: 42 }, (_, index) => {
        const date = new Date(start.getTime() + index * 86400000);
        const key = date.getUTCFullYear() + '-' + String(date.getUTCMonth() + 1).padStart(2, '0') + '-' + String(date.getUTCDate()).padStart(2, '0');
        const outside = String(date.getUTCMonth() + 1).padStart(2, '0') !== currentMonth;
        const dayEvents = this.events[this.activeGroup].filter((event) => this.eventDateKey(event) === key);
        const visible = dayEvents.slice(0, 3);
        const eventHtml = visible.map((event, eventIndex) => {
          const label = this.eventChipLabel(event);
          const time = this.formatTime(event.start);
          return '<button type="button" class="cbm-public-schedule__event' + (event.group === 'free' ? ' cbm-public-schedule__event--free' : '') + '" data-cbm-event-index="' + this.events[this.activeGroup].indexOf(event) + '">' +
            '<strong>' + this.escape(label) + '</strong><span>' + this.escape(time) + '</span></button>';
        }).join('');
        const more = dayEvents.length > visible.length
          ? '<span class="cbm-public-schedule__more">+' + (dayEvents.length - visible.length) + ' more</span>'
          : '';
        return '<div class="cbm-public-schedule__day' + (outside ? ' is-outside' : '') + (key === todayKey ? ' is-today' : '') + '" data-date="' + key + '">' +
          '<span class="cbm-public-schedule__day-number">' + date.getUTCDate() + '</span>' +
          eventHtml + more + '</div>';
      }).join('');

      this.grid.querySelectorAll('[data-cbm-event-index]').forEach((button) => {
        button.addEventListener('click', () => {
          const event = this.events[this.activeGroup][Number(button.dataset.cbmEventIndex)];
          if (event) this.openEvent(event);
        });
      });
    }

    renderAgenda(monthKey) {
      if (!this.agenda) return;
      const monthEvents = this.events[this.activeGroup].filter((event) => this.eventDateKey(event).slice(0, 7) === monthKey);
      if (!monthEvents.length) {
        this.agenda.innerHTML = '<div class="cbm-public-schedule__agenda-empty">' + this.escape(this.labels.empty_month || 'No classes are currently scheduled for this month.') + '</div>';
        return;
      }

      const grouped = {};
      monthEvents.forEach((event) => {
        const key = this.eventDateKey(event);
        (grouped[key] ||= []).push(event);
      });

      this.agenda.innerHTML = Object.entries(grouped).map(([key, events]) => {
        const date = new Date(key + 'T12:00:00');
        const dateLabel = new Intl.DateTimeFormat('en-US', {
          weekday: 'long',
          month: 'short',
          day: 'numeric',
          timeZone: this.timezone,
        }).format(date);
        return '<section class="cbm-public-schedule__agenda-day"><div class="cbm-public-schedule__agenda-date"><span>' + this.escape(dateLabel) + '</span><span>' + events.length + '</span></div>' +
          '<div class="cbm-public-schedule__agenda-events">' +
          events.map((event) => '<button type="button" class="cbm-public-schedule__agenda-event' + (event.group === 'free' ? ' is-free' : '') + '" data-cbm-agenda-session="' + this.escape(event.session_id || '') + '">' +
            '<span><strong>' + this.escape(this.eventChipLabel(event)) + '</strong><span>' + this.escape(event.title || '') + '</span></span><span>' + this.escape(this.formatTimeRange(event)) + '</span></button>').join('') +
          '</div></section>';
      }).join('');

      this.agenda.querySelectorAll('[data-cbm-agenda-session]').forEach((button) => {
        button.addEventListener('click', () => {
          const event = this.events[this.activeGroup].find((candidate) => String(candidate.session_id || '') === String(button.dataset.cbmAgendaSession || ''));
          if (event) this.openEvent(event);
        });
      });
    }

    eventChipLabel(event) {
      if (event.group === 'free') return (event.designation || event.title || 'Free CE') + (event.registration_status === 'FULL' ? ' · FULL' : '');
      return event.designation || event.title || 'Live CE';
    }

    formatDate(event) {
      const date = new Date(event?.start || '');
      if (Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: this.timezone,
      }).format(date);
    }

    formatTime(value) {
      const date = new Date(value || '');
      if (Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: this.timezone,
      }).format(date);
    }

    formatTimeRange(event) {
      const start = this.formatTime(event?.start);
      const end = this.formatTime(event?.end);
      return start + (end ? ' – ' + end : '') + ' PT';
    }

    openEvent(event) {
      if (!this.dialog) return;
      const isFree = event.group === 'free';
      const badge = this.querySelector('[data-cbm-dialog-badge]');
      const designation = this.querySelector('[data-cbm-dialog-designation]');
      const title = this.querySelector('[data-cbm-dialog-title]');
      const code = this.querySelector('[data-cbm-dialog-code]');
      const date = this.querySelector('[data-cbm-dialog-date]');
      const time = this.querySelector('[data-cbm-dialog-time]');
      const delivery = this.querySelector('[data-cbm-dialog-delivery]');
      const location = this.querySelector('[data-cbm-dialog-location]');
      const notes = this.querySelector('[data-cbm-dialog-notes]');
      const packageOffer = this.querySelector('[data-cbm-dialog-package]');
      const singlePath = this.querySelector('[data-cbm-dialog-single]');
      const pathCopy = this.querySelector('[data-cbm-dialog-path-copy]');
      const directLink = this.querySelector('[data-cbm-dialog-direct-link]');
      const packageLink = this.querySelector('[data-cbm-dialog-package-link]');

      if (badge) {
        badge.textContent = isFree ? (this.labels.free_badge || 'Free In-Person CE') : (this.labels.live_badge || 'Live Zoom CE');
        badge.classList.toggle('is-free', isFree);
      }
      if (designation) designation.textContent = event.designation || '';
      if (title) title.textContent = event.title || '';
      if (code) code.textContent = [event.course_code || '', event.ce_hours ? event.ce_hours + ' CE hours' : ''].filter(Boolean).join(' · ');
      if (date) date.textContent = this.formatDate(event);
      if (time) time.textContent = this.formatTimeRange(event);
      if (delivery) delivery.textContent = isFree ? 'In-Person' : 'Zoom Live';
      if (location) {
        location.textContent = isFree
          ? [event.venue_name, event.address, [event.city, event.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')
          : (this.labels.zoom_location || 'Zoom Only');
      }
      if (notes) {
        notes.hidden = !event.public_notes;
        notes.textContent = event.public_notes || '';
      }

      if (packageOffer) packageOffer.hidden = isFree;
      if (packageLink) packageLink.href = this.packageUrl;

      if (directLink && pathCopy && singlePath) {
        singlePath.hidden = false;
        singlePath.classList.toggle('is-free', isFree);
        if (isFree) {
          pathCopy.hidden = false;
          if (event.registration_status === 'FULL') {
            pathCopy.textContent = event.public_notes || 'FULL';
            directLink.hidden = true;
            directLink.removeAttribute('href');
            directLink.removeAttribute('target');
            directLink.removeAttribute('rel');
          } else if (event.registration_url) {
            pathCopy.textContent = event.public_notes || '';
            directLink.textContent = this.labels.reserve_cta || 'Reserve My Seat';
            directLink.href = event.registration_url;
            directLink.target = '_blank';
            directLink.rel = 'noopener noreferrer';
            directLink.hidden = false;
          } else {
            pathCopy.textContent = this.labels.free_no_link || 'Reservation details are being finalized for this complimentary class.';
            directLink.textContent = this.labels.contact_cta || 'Contact Us';
            directLink.href = this.contactUrl;
            directLink.removeAttribute('target');
            directLink.removeAttribute('rel');
            directLink.hidden = false;
          }
        } else {
          pathCopy.hidden = true;
          pathCopy.textContent = '';
          directLink.textContent = this.labels.single_cta || 'No, I just need this class';
          const productUrl = event.product?.url || '';
          directLink.href = productUrl ? this.productHandoffUrl(productUrl, event) : this.catalogUrl;
          directLink.removeAttribute('target');
          directLink.removeAttribute('rel');
          directLink.hidden = false;
        }
      }

      if (this.dialog.open) this.dialog.close();
      this.dialog.showModal();
    }

    productHandoffUrl(productUrl, event) {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('cbm_source', 'calendar');
      url.searchParams.set('cbm_course', event.course_code || '');
      url.searchParams.set('cbm_session', event.session_id || '');
      return url.pathname + url.search + url.hash;
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message || '';
    }

    escape(value) {
      const node = document.createElement('div');
      node.textContent = String(value ?? '');
      return node.innerHTML;
    }
  });
}

